import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button';

// ── Size map ──────────────────────────────────────────────────────────────────
// Sizes are CSS classes (not inline styles) so responsive breakpoints can
// override them — inline maxWidth defeats @media rules.
type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';

// ── Modal ─────────────────────────────────────────────────────────────────────

// Open modals in the order they opened — the last entry is the one Escape closes.
const openModalStack: symbol[] = [];

interface ModalProps {
  open:      boolean;
  onClose:   () => void;
  title?:    ReactNode;
  subtitle?: ReactNode;
  /** Optional third header column, right of title/subtitle and left of the close button (e.g. a fact panel) — top-aligned with them, same as the close button, so a taller block here never pushes the close button out of line. */
  headerRight?: ReactNode;
  size?:     ModalSize;
  children:  ReactNode;
  footer?:   ReactNode;
  /** Strip the body's default padding/scroll so children can lay out a custom full-bleed shell (e.g. a side-nav + content pane). */
  noPadding?: boolean;
  /** Skip .modal-head entirely — title/subtitle/headerRight and the built-in
   *  × all go with it. For a body that builds its own identity/close/actions
   *  row (e.g. a profile-banner header) so there's exactly one of each
   *  instead of one in .modal-head and a second, redundant one in the body.
   *  Escape-to-close still works (bound independently of .modal-head). */
  hideHead?: boolean;
  /** Trims a size's max-width down slightly (see .modal-narrow in index.css)
   *  for one modal that wants a bit less than the next size down but not as
   *  little as the size below that. Deliberately NOT an inline maxWidth —
   *  see the note at the top of this file on why that defeats the
   *  responsive/mobile-full-screen rules; this is scoped to only apply
   *  above the viewport width where those rules take over instead. */
  narrow?: boolean;
}

export function Modal({ open, onClose, title, subtitle, headerRight, size = 'md', children, footer, noPadding, hideHead, narrow }: ModalProps) {
  // Close on Escape — only the topmost open modal reacts, so a modal stacked
  // on another (Create Entity over Add Sale, a Discard-changes confirm over
  // its form) closes alone instead of every modal underneath it too.
  // onClose via a ref so the effect depends on `open` alone — callers pass an
  // inline arrow, and re-running on every render would re-push this modal to
  // the top of the stack above one opened on top of it.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });
  useEffect(() => {
    if (!open) return;
    const id = Symbol();
    openModalStack.push(id);
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openModalStack[openModalStack.length - 1] === id) onCloseRef.current();
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      openModalStack.splice(openModalStack.indexOf(id), 1);
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`modal modal-structured modal-${size}${narrow ? ' modal-narrow' : ''}`}>
        {/* Header */}
        {!hideHead && (
          <div className="modal-head">
            <div>
              <h3>{title}</h3>
              {subtitle && <div className="modal-head-sub">{subtitle}</div>}
            </div>
            {headerRight && <div className="modal-head-right">{headerRight}</div>}
            <button className="modal-x" onClick={onClose} title="Close">✕</button>
          </div>
        )}

        {/* Scrollable body */}
        <div className={`modal-body${noPadding ? ' modal-body--flush' : ''}`}>
          {children}
        </div>

        {/* Sticky footer */}
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

// ── ConfirmModal — quick delete/confirm dialog ─────────────────────────────────

interface ConfirmModalProps {
  open:      boolean;
  onClose:   () => void;
  onConfirm: () => void;
  title:     string;
  message:   ReactNode;
  confirmLabel?: string;
  loading?:  boolean;
  error?:    string;
}

export function ConfirmModal({
  open, onClose, onConfirm,
  title, message,
  confirmLabel = 'Confirm',
  loading, error,
}: ConfirmModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="danger" onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
        </>
      }
    >
      <p style={{ color: 'var(--text-2)', fontSize: 13, lineHeight: 1.6 }}>{message}</p>
      {error && <div className="msg msg-error" style={{ marginTop: 14 }}>{error}</div>}
    </Modal>
  );
}
